import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    AlertTriangle, ArrowLeft, CalendarDays, CheckCircle2, ClipboardCheck, Info,
    Loader2, MapPin, Plus, Save, Send, TriangleAlert, X,
} from 'lucide-react';
import { Button } from '../../Button';
import { ChecklistView } from '../../ChecklistView';
import { InspectionItemCard } from '../../inspection-list/InspectionItemCard';
import { ChecklistEntry, Contact, InspectionItem } from '../../../types';
import { describeError } from '../../../services/errorMessage';
import { registerBauphaseFlusher } from '../../../services/bauphaseFlush';
import { holdIdleExit } from '../../../services/idleHold';
import {
    ASSIGNMENT_STATUS_KLASSEN,
    ASSIGNMENT_STATUS_LABELS,
    AssignmentStatus,
    formatZeitraum,
} from '../../../services/assignmentLogic';
import { heuteIso } from '../../../services/freelancerLogic';
import {
    neuenEintragEinfuegen,
    statusWechselAnwenden,
} from '../../../services/inspectionItemStatus';
import {
    BEGEHUNGS_SCHRITT_LABELS,
    FreelancerInspectionData,
    begehungsZahlen,
    buildInspectionPayload,
    einreichHindernisse,
    naechsterBegehungsSchritt,
} from '../../../services/freelancerInspectionLogic';
import {
    ladeBegehung,
    ladeBegehungsFotoHoch,
    setzeAuftragsStatus,
    speichereBegehung,
} from '../../../services/freelancerPortalService';
import {
    ausgangNachSpeichern,
    nichtsDazugekommen,
    registriereVerlassenWache,
    rueckfrageVorVerlassen,
    verlassenLeiste,
    type LeistenErgebnis,
} from './begehungVerlassenLogik';

// Die Begehungs-Durchführung im Freelancer-Portal (ERP/CRM-Plan Abschnitt 7,
// „Workflow" Schritt 3 — Phase D4).
//
// ─── Wiederverwendung statt Nachbau ────────────────────────────────────────
// Die beiden Erfassungsflächen sind die BESTANDS-Komponenten des internen
// Betriebs:
//   • `ChecklistView` — unverändert, inklusive Kategorie-Filter, eigenen
//     Prüfpunkten und der Master-Vorlage aus checklistLogic.
//   • `InspectionItemCard` — unverändert, inklusive Foto-Aufnahme, Status,
//     Frist und KI-Textassistenz.
// Ein reduzierter Nachbau hätte zwei Oberflächen für dieselbe Sache ergeben,
// die auseinanderlaufen, sobald eine von beiden gepflegt wird. Was hier
// entsteht, ist deshalb nur der CONTAINER: laden, halten, hochladen,
// zurückschreiben.
//
// ─── Zwei bewusste Abweichungen vom internen Verhalten ─────────────────────
// 1. FOTOS gehen in den AUFTRAGSPFAD `<project_id>/freelancer/<assignment_id>`
//    (ladeBegehungsFotoHoch), nicht in den Projektordner. Nur für dieses
//    Präfix erlaubt die Storage-Policy dem Freelancer ein INSERT.
// 2. LÖSCHEN gibt es nicht. `freelancer_save_inspection` macht bewusst kein
//    Prune (Begründung im SQL, Abschnitt 7): ein Client mit unvollständigem
//    Stand könnte sonst interne Arbeit entfernen. Ein Mangel, der weg soll,
//    wird auf „erledigt" gesetzt. Entfernen lässt sich nur, was in DIESER
//    Sitzung neu angelegt und noch nicht gespeichert wurde.
//
// Der Container ist dünn (Standing Decision): jede fachliche Aussage — was der
// nächste Schritt ist, was das Einreichen blockiert, wie gezählt wird — kommt
// fertig aus freelancerInspectionLogic.ts.

interface Props {
    assignmentId: string;
    /** Zurück in die Auftragsliste. */
    onBack: () => void;
    /** Nach einem Statuswechsel: die Auftragsliste des Portals neu laden. */
    onStatusChanged?: () => void;
}

type Bereich = 'checkliste' | 'maengel';

/** Tipp auf „Auf Plan verorten": das Portal liefert keine Pläne aus. */
const PLAN_HINWEIS =
    'Die Verortung auf dem Plan gibt es im Portal nicht – bitte den Ort unter „Bereich / Stockwerk" angeben.';

export const FreelancerInspection: React.FC<Props> = ({ assignmentId, onBack, onStatusChanged }) => {
    const [daten, setDaten] = useState<FreelancerInspectionData | null>(null);
    const [checklists, setChecklists] = useState<ChecklistEntry[]>([]);
    const [items, setItems] = useState<InspectionItem[]>([]);
    const [sectionId, setSectionId] = useState<string>('all');
    const [bereich, setBereich] = useState<Bereich>('checkliste');
    const [laedt, setLaedt] = useState(true);
    const [speichert, setSpeichert] = useState(false);
    const [schrittLaeuft, setSchrittLaeuft] = useState(false);
    const [fehler, setFehler] = useState<string | null>(null);
    const [hinweis, setHinweis] = useState<string | null>(null);
    const [expanded, setExpanded] = useState<string | null>(null);
    const [ungespeichert, setUngespeichert] = useState(false);
    /** Laufende Foto-Uploads: bis sie fertig sind, fehlt das Foto im Mangel. */
    const [fotosLaufen, setFotosLaufen] = useState(0);
    /** Offene Rückfrage „Ungespeicherte Änderungen": wohin es danach geht. */
    const [verlassenFrage, setVerlassenFrage] = useState<{ weiter: () => void; ergebnis: LeistenErgebnis } | null>(null);
    /** Hinweis zum Plan-Knopf der Karte, als Leiste unten beim Daumen. */
    const [planHinweis, setPlanHinweis] = useState(false);
    /**
     * Stand der Änderungen: jede Änderung zählt hoch. `speichern` vergleicht
     * ihn vor und nach dem Warten (begehungVerlassenLogik).
     */
    const aenderungen = useRef(0);
    /**
     * Neue Frage, „Abbrechen" und „Verwerfen" zählen hoch: ein laufendes
     * Speichern aus der Leiste leitet nur weiter, wenn seine Frage noch steht.
     */
    const verlassenLauf = useRef(0);

    /**
     * Die ids, die beim Laden vom Server kamen. Alles darin ist nur noch
     * änderbar, nicht mehr entfernbar (s. Kopfkommentar, Punkt 2).
     */
    const serverIds = useRef<Set<string>>(new Set());

    const laden = useCallback(async () => {
        setLaedt(true);
        setFehler(null);
        try {
            const d = await ladeBegehung(assignmentId);
            setDaten(d);
            setChecklists(d?.checklists ?? []);
            setItems(d?.items ?? []);
            serverIds.current = new Set((d?.items ?? []).map((i) => i.id));
            setUngespeichert(false);
        } catch (e) {
            setFehler(describeError(e));
        } finally {
            setLaedt(false);
        }
    }, [assignmentId]);

    useEffect(() => {
        void laden();
    }, [laden]);

    // Begehungsdatum des Portals.
    //
    // `FreelancerInspectionData` traegt nur den Leistungszeitraum des Auftrags
    // (zeitraumVon/-Bis), kein Datum der Durchfuehrung — die Begehung hat
    // keinen eigenen Kalendertag, solange sie laeuft. Fuer den Freelancer ist
    // das auch richtig: er erfasst waehrend der Begehung, heute IST sein
    // Begehungstag. Das interne Nacharbeiten Tage spaeter passiert im
    // Bestandsmodul, und das kennt report.metadata.date.
    const begehungsDatum = heuteIso();
    const zahlen = useMemo(() => begehungsZahlen(checklists, items), [checklists, items]);
    const hindernisse = useMemo(() => einreichHindernisse(items), [items]);
    const schritt = useMemo(
        () => naechsterBegehungsSchritt(daten?.status),
        [daten?.status],
    );
    const bearbeitbar = schritt !== null;
    /** Ginge beim Verlassen etwas verloren, das sich speichern ließe? */
    const rueckfrage = rueckfrageVorVerlassen(ungespeichert, bearbeitbar, fotosLaufen);

    /** Die Firmenliste des Projekts als Contact-Form für InspectionItemCard.
     *  Die Karte liest daraus ausschliesslich `company` (Vorschlagsliste
     *  „Verantwortliche Firma"); E-Mail und Telefon liefert die RPC bewusst
     *  nicht mit. */
    const kontakte = useMemo<Contact[]>(
        () =>
            (daten?.companies ?? []).map((k, idx) => ({
                id: `firma-${idx}`,
                name: '',
                company: k.company,
                role: k.role || '',
                email: '',
                tradeType: k.tradeType,
                present: false,
                emailDistribution: false,
            })),
        [daten?.companies],
    );

    const sichtbareItems = useMemo(
        () =>
            sectionId === 'all'
                ? items
                : items.filter((i) => (i.sectionId || '') === sectionId),
        [items, sectionId],
    );

    // ── Änderungen ──────────────────────────────────────────────────────────
    const geaendert = () => {
        aenderungen.current += 1;
        setUngespeichert(true);
    };

    const checklistsSetzen = (neu: ChecklistEntry[]) => {
        setChecklists(neu);
        geaendert();
    };

    const itemSetzen = (id: string, aenderung: Partial<InspectionItem>) => {
        setItems((prev) => prev.map((i) => (i.id === id ? { ...i, ...aenderung } : i)));
        geaendert();
    };

    // Statuswechsel laeuft ueber denselben Helfer wie die interne Maengelliste:
    // er setzt das Freimeldedatum UND die Nummernregel (Information traegt
    // keine laufende Nummer).
    const statusSetzen = (id: string, status: InspectionItem['status']) => {
        setItems(prev => statusWechselAnwenden(prev, id, status, begehungsDatum));
        geaendert();
    };

    const mangelHinzufuegen = () => {
        const neu: InspectionItem = {
            id: crypto.randomUUID(),
            title: '',
            location: '',
            observation: '',
            regulation: '',
            responsiblePerson: '',
            deadline: '',
            dateFound: begehungsDatum,
            status: 'open',
            photos: [],
            sectionId: sectionId !== 'all' ? sectionId : undefined,
        };
        // Nummer aus dem Bestand des Updaters, nicht aus der Closure: dieselbe
        // Regel wie im mobilen Wizard — die laufende Nummer entsteht beim
        // Einfuegen, nie vorher.
        setItems((prev) => neuenEintragEinfuegen(prev, neu, 'hinten'));
        setExpanded(neu.id);
        setBereich('maengel');
        geaendert();
    };

    const mangelEntfernen = (id: string) => {
        if (serverIds.current.has(id)) {
            setHinweis(
                'Ein bereits gespeicherter Mangel lässt sich hier nicht löschen. Setzen Sie ihn '
                + 'stattdessen auf „Erledigt" — der Innendienst sieht dann, was aus ihm geworden ist.',
            );
            return;
        }
        setItems((prev) => prev.filter((i) => i.id !== id));
        geaendert();
    };

    const fotosHinzufuegen = async (id: string, base64s: string[]) => {
        if (!daten) return;
        setFehler(null);
        // Solange hochgeladen wird, fragt das Verlassen nach: sonst endete der
        // Upload in der abgebauten Maske, und das Foto fehlte im Mangel.
        setFotosLaufen((n) => n + 1);
        try {
            const urls: string[] = [];
            for (const b of base64s) {
                urls.push(await ladeBegehungsFotoHoch(b, daten.projekt.id, daten.assignmentId));
            }
            setItems((prev) =>
                prev.map((i) => (i.id === id ? { ...i, photos: [...(i.photos || []), ...urls] } : i)),
            );
            geaendert();
        } catch (e) {
            setFehler(describeError(e));
        } finally {
            setFotosLaufen((n) => Math.max(0, n - 1));
        }
    };

    const fotoEntfernen = (id: string, index: number) => {
        setItems((prev) =>
            prev.map((i) =>
                i.id === id ? { ...i, photos: (i.photos || []).filter((_, k) => k !== index) } : i,
            ),
        );
        geaendert();
    };

    // ── Speichern und Einreichen ────────────────────────────────────────────
    const speichern = async (still = false): Promise<boolean> => {
        setSpeichert(true);
        setFehler(null);
        // Die Nutzlast entsteht jetzt. Was während des Wartens dazukommt, ist
        // nicht darin und bleibt ungespeichert.
        const stand = aenderungen.current;
        try {
            const ergebnis = await speichereBegehung(
                assignmentId,
                buildInspectionPayload(checklists, items),
            );
            serverIds.current = new Set(items.map((i) => i.id));
            if (nichtsDazugekommen(stand, aenderungen.current)) setUngespeichert(false);
            if (!still) {
                setHinweis(
                    `Gespeichert: ${ergebnis.checklists} Prüfpunkte, ${ergebnis.items} Mängel.`,
                );
            }
            return true;
        } catch (e) {
            setFehler(describeError(e));
            return false;
        } finally {
            setSpeichert(false);
        }
    };

    // ── Sicherung vor der Bauphase ──────────────────────────────────────────
    // Maengel und Pruefpunkte liegen bis zum Klick auf „Zwischenspeichern"
    // NUR hier im State; der Flush des Bauphase-Gates (App.tsx) kennt sie
    // nicht. Solange etwas ungespeichert ist, meldet sich dieser Baum als
    // Sicherung an — das Gate ruft sie, bevor die Wartungsseite den Baum
    // ersetzt. `speichern` ist eine schlichte Closure ueber den aktuellen
    // State, daher der Ref: die Anmeldung bleibt ueber Renders stehen, ruft
    // aber immer die juengste Fassung mit dem juengsten Stand.
    const speichernRef = useRef(speichern);
    speichernRef.current = speichern;
    useEffect(() => {
        if (!ungespeichert) return;
        return registerBauphaseFlusher(`freelancer-inspection:${assignmentId}`, async () => {
            await speichernRef.current(true);
        });
    }, [ungespeichert, assignmentId]);

    // ── Rückfrage vor dem Verlassen ─────────────────────────────────────────
    // Ohne Autosave verwarfen „Meine Aufträge", die Reiter des Portals und
    // „Abmelden" ungespeicherte Prüfpunkte und Mängel wortlos. „Meine Aufträge"
    // fragt hier direkt nach; für den Kopf des Portals steht die Wache
    // (begehungVerlassenLogik) bereit, angemeldet, solange etwas offen ist.
    const frageOeffnen = useCallback((weiter: () => void) => {
        verlassenLauf.current += 1;
        setPlanHinweis(false);
        setVerlassenFrage({ weiter, ergebnis: null });
    }, []);

    useEffect(() => {
        if (!rueckfrage) return;
        return registriereVerlassenWache(frageOeffnen);
    }, [rueckfrage, frageOeffnen]);

    // Solange etwas offen ist, auch kein automatisches Neuladen bei einer neuen
    // Version: die Erfassung liegt nur im Speicher (App: 'defer' statt 'reload').
    useEffect(() => {
        if (!rueckfrage) return;
        return holdIdleExit('freelancer-begehung-ungespeichert');
    }, [rueckfrage]);

    const verlassen = (weiter: () => void) => {
        if (rueckfrage) frageOeffnen(weiter);
        else weiter();
    };

    const verlassenVerwerfen = () => {
        const frage = verlassenFrage;
        verlassenLauf.current += 1;
        setVerlassenFrage(null);
        frage?.weiter();
    };

    // Auch während des Speicherns: die Leiste darf keine Sackgasse sein.
    const verlassenAbbrechen = () => {
        verlassenLauf.current += 1;
        setVerlassenFrage(null);
    };

    // Derselbe Speicherweg wie „Zwischenspeichern". Weiter geht es erst, wenn
    // er geklappt hat UND nichts dazukam — sonst bleibt die Frage stehen und
    // sagt das.
    const verlassenSpeichern = async () => {
        const frage = verlassenFrage;
        if (!frage || fotosLaufen > 0) return;
        if (!ungespeichert) {
            // Inzwischen gespeichert (etwa über „Zwischenspeichern").
            setVerlassenFrage(null);
            frage.weiter();
            return;
        }
        verlassenLauf.current += 1;
        const lauf = verlassenLauf.current;
        const stand = aenderungen.current;
        const gespeichert = await speichern(true);
        // Abgebrochen, verworfen oder neu gefragt, während gespeichert wurde.
        if (verlassenLauf.current !== lauf) return;
        const ausgang = ausgangNachSpeichern(gespeichert, stand, aenderungen.current);
        if (ausgang === 'weiter') {
            setVerlassenFrage(null);
            frage.weiter();
        } else {
            setVerlassenFrage({ ...frage, ergebnis: ausgang });
        }
    };

    const schrittAusloesen = async () => {
        if (!schritt) return;
        // Beim Einreichen zuerst prüfen: ein unvollständiger offener Mangel ist
        // im Bericht eine leere Zeile — dieselbe Regel wie im internen Versand.
        if (schritt === 'bericht_eingereicht' && hindernisse.length > 0) {
            setFehler(hindernisse.join(' '));
            setBereich('maengel');
            return;
        }
        setSchrittLaeuft(true);
        setFehler(null);
        try {
            // IMMER erst speichern: der Status ist die Aussage über den Stand,
            // und ein Bericht, dessen letzte Änderung nie ankam, wäre eine
            // falsche Aussage.
            const gespeichert = await speichern(true);
            if (!gespeichert) return;

            await setzeAuftragsStatus(assignmentId, schritt);
            onStatusChanged?.();
            await laden();
            setHinweis(
                schritt === 'durchgefuehrt'
                    ? 'Die Begehung ist als durchgeführt vermerkt. Sie können den Bericht jetzt einreichen.'
                    : 'Der Bericht ist eingereicht. Der Innendienst prüft ihn und gibt ihn frei.',
            );
        } catch (e) {
            setFehler(describeError(e));
        } finally {
            setSchrittLaeuft(false);
        }
    };

    // ── Zustände ────────────────────────────────────────────────────────────
    if (laedt) {
        return (
            <div className="flex items-center justify-center py-20 text-slate-400">
                <Loader2 className="w-6 h-6 animate-spin" />
            </div>
        );
    }

    if (!daten) {
        return (
            <div className="space-y-4">
                <Button variant="secondary" onClick={onBack} icon={<ArrowLeft className="w-4 h-4" />}>
                    Zurück
                </Button>
                <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-xl p-4 text-sm">
                    {fehler || 'Diese Begehung lässt sich nicht öffnen.'}
                </div>
            </div>
        );
    }

    const leiste = verlassenFrage
        ? verlassenLeiste(verlassenFrage.ergebnis, ungespeichert, fotosLaufen, fehler)
        : null;

    return (
        <div className="space-y-4">
            {/* ── Kopf ── */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 space-y-3">
                <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                        <button
                            onClick={() => verlassen(onBack)}
                            className="text-[11px] font-bold text-slate-400 hover:text-slate-700 inline-flex items-center gap-1 mb-1 min-h-[44px] pr-2 md:min-h-0 md:pr-0"
                        >
                            <ArrowLeft className="w-3.5 h-3.5" />
                            Meine Aufträge
                        </button>
                        <h3 className="font-bold text-slate-800 text-base truncate">
                            {daten.projekt.name}
                        </h3>
                        {daten.projekt.projectNumber && (
                            <p className="text-[11px] text-slate-400 font-mono">
                                {daten.projekt.projectNumber}
                            </p>
                        )}
                    </div>
                    <span
                        className={`inline-flex items-center px-2.5 py-1 rounded-lg text-[11px] font-bold border shrink-0 ${
                            ASSIGNMENT_STATUS_KLASSEN[daten.status as AssignmentStatus]
                        }`}
                    >
                        {ASSIGNMENT_STATUS_LABELS[daten.status as AssignmentStatus]}
                    </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs text-slate-600">
                    <span className="flex items-center gap-2">
                        <CalendarDays className="w-3.5 h-3.5 shrink-0 text-slate-400" />
                        {formatZeitraum(daten.zeitraumVon, daten.zeitraumBis)}
                    </span>
                    <span className="flex items-center gap-2 min-w-0">
                        <MapPin className="w-3.5 h-3.5 shrink-0 text-slate-400" />
                        <span className="truncate">{daten.projekt.address || 'Keine Adresse hinterlegt'}</span>
                    </span>
                    <span className="flex items-center gap-2">
                        <ClipboardCheck className="w-3.5 h-3.5 shrink-0 text-slate-400" />
                        {zahlen.geprueft}/{zahlen.geprueft + zahlen.offen} geprüft · {zahlen.maengelOffen} offene Mängel
                    </span>
                </div>

                {daten.sections.length > 0 && (
                    <div className="flex flex-wrap items-center gap-2 pt-1">
                        <span className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">
                            Abschnitt
                        </span>
                        {[{ id: 'all', name: 'Alle' }, ...daten.sections].map((s) => (
                            <button
                                key={s.id}
                                onClick={() => setSectionId(s.id)}
                                className={`px-3 py-1 rounded-full text-[11px] font-bold border transition-colors ${
                                    sectionId === s.id
                                        ? 'bg-slate-800 text-white border-slate-800'
                                        : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'
                                }`}
                            >
                                {s.name}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {/* ── Prüfkommentar der Nachbesserung ── */}
            {daten.status === 'nachbesserung' && daten.pruefkommentar && (
                <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-start gap-3">
                    <TriangleAlert className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                    <div className="min-w-0">
                        <p className="text-sm font-bold text-amber-900">Nachbesserung angefordert</p>
                        <p className="text-sm text-amber-800 leading-relaxed mt-1 whitespace-pre-line">
                            {daten.pruefkommentar}
                        </p>
                        <p className="text-[11px] text-amber-700 mt-2">
                            Bitte die genannten Punkte ergänzen und den Bericht erneut einreichen.
                        </p>
                    </div>
                </div>
            )}

            {fehler && (
                <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-xl p-3 text-xs flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                    <span className="flex-1">{fehler}</span>
                    <button onClick={() => setFehler(null)} className="shrink-0 hover:bg-rose-100 rounded p-1">
                        <X className="w-4 h-4" />
                    </button>
                </div>
            )}

            {hinweis && (
                <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl p-3 text-xs flex items-start gap-2">
                    <Info className="w-4 h-4 shrink-0 mt-0.5" />
                    <span className="flex-1">{hinweis}</span>
                    <button onClick={() => setHinweis(null)} className="shrink-0 hover:bg-emerald-100 rounded p-1">
                        <X className="w-4 h-4" />
                    </button>
                </div>
            )}

            {!bearbeitbar && (
                <div className="bg-slate-50 border border-slate-200 text-slate-600 rounded-xl p-3 text-xs flex items-start gap-2">
                    <Info className="w-4 h-4 shrink-0 mt-0.5" />
                    <span>
                        Dieser Auftrag ist nicht mehr in Bearbeitung — die Erfassung ist damit
                        abgeschlossen. Änderungen lassen sich nicht mehr speichern.
                    </span>
                </div>
            )}

            {/* ── Bereichswahl ── */}
            <div className="inline-flex rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm">
                {([
                    { key: 'checkliste' as const, label: 'Prüfpunkte', zahl: zahlen.offen },
                    { key: 'maengel' as const, label: 'Mängel', zahl: zahlen.maengelOffen },
                ]).map((b) => (
                    <button
                        key={b.key}
                        type="button"
                        onClick={() => setBereich(b.key)}
                        aria-pressed={bereich === b.key}
                        className={`flex items-center gap-2 px-5 py-2.5 text-sm font-bold whitespace-nowrap border-b-2 transition-all ${
                            bereich === b.key
                                ? 'border-blue-600 text-blue-600 bg-blue-50/50'
                                : 'border-transparent text-slate-400 hover:text-slate-600 hover:bg-slate-50'
                        }`}
                    >
                        {b.label}
                        {b.zahl > 0 && (
                            <span className="bg-slate-200 text-slate-600 rounded-full px-1.5 py-0.5 text-[11px] leading-none">
                                {b.zahl}
                            </span>
                        )}
                    </button>
                ))}
            </div>

            {/* ── Erfassung ── */}
            {bereich === 'checkliste' ? (
                <ChecklistView
                    entries={checklists}
                    onChange={checklistsSetzen}
                    reportDate={begehungsDatum}
                    activeSectionId={sectionId}
                />
            ) : (
                <div className="space-y-3">
                    <div className="flex justify-between items-center">
                        <p className="text-xs text-slate-500">
                            {sichtbareItems.length} {sichtbareItems.length === 1 ? 'Eintrag' : 'Einträge'}
                        </p>
                        <Button
                            variant="primary"
                            onClick={mangelHinzufuegen}
                            disabled={!bearbeitbar}
                            icon={<Plus className="w-4 h-4" />}
                            className="text-xs py-1.5"
                        >
                            Mangel erfassen
                        </Button>
                    </div>

                    {sichtbareItems.length === 0 ? (
                        <div className="text-center py-12 text-slate-400 italic bg-white rounded-xl border border-dashed">
                            Noch kein Mangel erfasst.
                        </div>
                    ) : (
                        sichtbareItems.map((item) => (
                            <InspectionItemCard
                                key={item.id}
                                item={item}
                                isExpanded={expanded === item.id}
                                onToggleExpand={() => setExpanded(expanded === item.id ? null : item.id)}
                                onUpdate={(id, feld, wert) =>
                                    itemSetzen(id, { [feld]: wert } as Partial<InspectionItem>)}
                                onPatch={itemSetzen}
                                onDelete={mangelEntfernen}
                                // Nicht nur der Status: „Erledigt" ohne
                                // Erledigungsdatum kommt im Bericht als leere
                                // Zeile an (closed_at bleibt NULL). Das Portal
                                // hat keinen Freimelde-Dialog, deshalb setzt
                                // der Klick das Datum gleich mit.
                                onStatusChange={statusSetzen}
                                onAddPhotos={fotosHinzufuegen}
                                onRemovePhoto={fotoEntfernen}
                                // Planverortung gehört zur internen Planpflege; das
                                // Portal liefert keine Pläne aus (s. RPC-Spaltenauswahl).
                                // Die Karte zeigt den Knopf trotzdem — der Tipp darauf
                                // erklärt das in einer Leiste unten, nicht im Band oben:
                                // der Knopf sitzt tief in der aufgeklappten Karte.
                                onPinOnPlan={() => setPlanHinweis(true)}
                                // KI-Textassistenz ist im Portal nicht angebunden: der
                                // Aufruf liefe über den gemini-proxy mit internem
                                // Kontext. Die Karte blendet den Knopf nicht aus; sie
                                // zeigt die Meldung des Fehlers unter dem Knopf.
                                onAiAssist={async () => {
                                    throw new Error('KI-Vorschläge gibt es im Portal nicht – bitte selbst formulieren.');
                                }}
                                contacts={kontakte}
                                availablePlans={[]}
                            />
                        ))
                    )}
                </div>
            )}

            {/* ── Fußleiste ── */}
            {bearbeitbar && (
                <div className="sticky bottom-0 bg-white/90 backdrop-blur border-t border-slate-200 -mx-4 px-4 py-3 flex flex-wrap items-center gap-3 justify-end">
                    {ungespeichert && (
                        <span className="text-[11px] text-amber-700 mr-auto">
                            Nicht gespeicherte Änderungen
                        </span>
                    )}
                    <Button
                        variant="secondary"
                        onClick={() => void speichern()}
                        disabled={speichert || schrittLaeuft}
                        icon={speichert ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    >
                        Zwischenspeichern
                    </Button>
                    <Button
                        variant="primary"
                        onClick={schrittAusloesen}
                        // Ein laufender Foto-Upload fehlte sonst im eingereichten Bericht.
                        disabled={speichert || schrittLaeuft || fotosLaufen > 0}
                        icon={
                            schrittLaeuft ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : schritt === 'durchgefuehrt' ? (
                                <CheckCircle2 className="w-4 h-4" />
                            ) : (
                                <Send className="w-4 h-4" />
                            )
                        }
                    >
                        {schritt ? BEGEHUNGS_SCHRITT_LABELS[schritt] : ''}
                    </Button>
                </div>
            )}

            {/* ── Leisten am unteren Rand ──
                Rückfrage vor dem Verlassen und Hinweis zum Plan-Knopf, wie im
                Mangel-Assistenten: beim Daumen und sichtbar, wie weit auch
                gescrollt ist — die Reiter des Portals kleben oben und sind von
                überall erreichbar. Höchstens eine zugleich, als letztes Kind,
                damit `space-y-4` ihr keinen Rand gibt. */}
            {leiste ? (
                <div
                    role="alertdialog"
                    aria-labelledby="begehung-verlassen-titel"
                    className="fixed bottom-0 left-0 right-0 z-20 bg-white p-4 border-t border-slate-200 shadow-lg pb-safe"
                >
                    <div className="max-w-4xl mx-auto space-y-3">
                        <div>
                            <p id="begehung-verlassen-titel" className="font-bold text-slate-800">
                                {leiste.titel}
                            </p>
                            <p className="text-xs text-slate-500 mt-0.5">{leiste.text}</p>
                            {leiste.grund && (
                                <p className="text-xs text-rose-700 mt-1">{leiste.grund}</p>
                            )}
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                            <button
                                type="button"
                                onClick={verlassenVerwerfen}
                                // Auch während des Speicherns: hängt es, bleibt sonst nur App beenden.
                                disabled={!leiste.nochOffen}
                                className="min-h-[44px] py-3 bg-slate-100 rounded-xl text-sm font-semibold text-red-700 disabled:opacity-50"
                            >
                                Verwerfen
                            </button>
                            <button
                                type="button"
                                onClick={verlassenAbbrechen}
                                className="min-h-[44px] py-3 bg-white border border-slate-200 rounded-xl text-sm font-semibold text-slate-700"
                            >
                                Abbrechen
                            </button>
                            <button
                                type="button"
                                onClick={() => void verlassenSpeichern()}
                                disabled={speichert || schrittLaeuft || fotosLaufen > 0}
                                className="min-h-[44px] py-3 bg-blue-600 text-white rounded-xl text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50"
                            >
                                {speichert ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : leiste.nochOffen ? (
                                    <Save className="w-4 h-4" />
                                ) : (
                                    <CheckCircle2 className="w-4 h-4" />
                                )}
                                {leiste.nochOffen ? 'Speichern' : 'Weiter'}
                            </button>
                        </div>
                    </div>
                </div>
            ) : planHinweis ? (
                <div
                    role="status"
                    className="fixed bottom-0 left-0 right-0 z-20 bg-white p-4 border-t border-slate-200 shadow-lg pb-safe"
                >
                    <div className="max-w-4xl mx-auto flex items-center gap-3">
                        <Info className="w-5 h-5 shrink-0 text-slate-400" />
                        <p className="flex-1 text-xs text-slate-600">{PLAN_HINWEIS}</p>
                        <button
                            type="button"
                            onClick={() => setPlanHinweis(false)}
                            className="min-h-[44px] px-5 bg-slate-100 rounded-xl text-sm font-semibold text-slate-700"
                        >
                            OK
                        </button>
                    </div>
                </div>
            ) : null}
        </div>
    );
};
